const normalizeLabel = (value = '') => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toLowerCase();

const dictionaryLabels = (entry, fallback) => {
  const values = [
    entry?.menu,
    entry?.catalog,
    entry?.display,
    typeof entry === 'string' ? entry : null,
    fallback,
  ];

  return new Set(values.map(normalizeLabel).filter(Boolean));
};

const webmanagerLabels = () => dictionaryLabels(
  window?.dic?.categories?.webmanager,
  'webmanager',
);

const siteManagerLabels = () => dictionaryLabels(
  window?.dic?.domains?.sitemanager,
  'sitemanager',
);

const matchesLabels = (item, labels) => {
  const label = item?.querySelector?.(':scope > div > span');
  return labels.has(normalizeLabel(label?.textContent));
};

const findWebmanagerBranch = (aside) => {
  const labels = webmanagerLabels();
  return Array.from(aside.querySelectorAll('.ep-menu-aside > li')).find((item) => (
    matchesLabels(item, labels)
  )) || null;
};

const findSiteManagerBranch = (webmanagerBranch) => {
  if (!webmanagerBranch) return null;
  const labels = siteManagerLabels();
  return Array.from(webmanagerBranch.querySelectorAll('li')).find((item) => (
    matchesLabels(item, labels)
  )) || null;
};

const ensureSubmenu = (item) => {
  if (!item) return null;

  let submenu = item.querySelector(':scope > ul.ep-submenu');
  if (submenu) return submenu;

  submenu = document.createElement('ul');
  submenu.className = 'ep-submenu';
  submenu.style.maxHeight = '0px';
  item.appendChild(submenu);

  const toggle = item.querySelector(':scope > div');
  if (toggle && !toggle.classList.contains('menu-toggle')) {
    toggle.classList.add('menu-toggle');
    toggle.addEventListener('click', (event) => {
      event.stopPropagation();
      const open = !item.classList.contains('open');
      item.classList.toggle('open', open);
      submenu.style.maxHeight = open ? '9999px' : '0px';
    });
  }

  return submenu;
};

const hideSingleSiteSelector = (context) => {
  const comboElement = context?.querySelector?.('.combo');
  const comboInstance = comboElement?.combo;
  const siteOptions = Array.isArray(comboInstance?.options?.options)
    ? comboInstance.options.options
    : [];

  if (siteOptions.length > 1) return false;

  comboInstance?.destroy?.();
  context?.remove?.();
  return true;
};

export default function mountWebmanagerSiteContext() {
  const aside = document.querySelector('aside');
  if (!aside || typeof MutationObserver === 'undefined') return () => {};

  let scheduled = false;

  const relocate = () => {
    const context = aside.querySelector('.webmanager-site-context');
    if (!context) return;

    // O backend já seleciona e persiste o primeiro site acessível. Com apenas
    // um site não existe escolha para o usuário, então o seletor não é UI útil.
    if (hideSingleSiteSelector(context)) return;

    const webmanagerBranch = findWebmanagerBranch(aside);
    if (!webmanagerBranch) return;

    // Há duas formas válidas de menu. Quando o domínio sitemanager aparece
    // explicitamente, o contexto pertence a ele. Quando o menu achata o domínio,
    // o contexto fica no topo do ramo webmanager.
    const targetBranch = findSiteManagerBranch(webmanagerBranch) || webmanagerBranch;
    const submenu = ensureSubmenu(targetBranch);
    if (!submenu) return;

    let holder = submenu.querySelector(':scope > li.webmanager-site-context-item');
    if (!holder) {
      holder = document.createElement('li');
      holder.className = 'webmanager-site-context-item';
      submenu.prepend(holder);
    }

    if (context.parentElement !== holder) {
      holder.appendChild(context);
    }
  };

  const scheduleRelocate = () => {
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      relocate();
    });
  };

  const observer = new MutationObserver(scheduleRelocate);
  observer.observe(aside, { childList: true, subtree: true });
  relocate();

  return () => observer.disconnect();
}
